<template>
  <main class="app-content">
    <div class="app-title">
      <h1><font-awesome-icon icon="comments"/> AI Agent - Lịch sử hội thoại</h1>
    </div>

    <!-- Filters -->
    <div class="row mb-3">
      <div class="col-md-2">
        <b-form-group label="Trạng thái">
          <b-form-select v-model="filter.hasError" :options="errorOptions" @change="load"/>
        </b-form-group>
      </div>
      <div class="col-md-3">
        <b-form-group label="Từ khóa">
          <b-form-input v-model="filter.keyword" placeholder="Tìm nội dung trả lời..." @keyup.enter="load"/>
        </b-form-group>
      </div>
      <div class="col-md-2">
        <b-form-group label="Từ ngày">
          <b-form-input type="date" v-model="filter.from"/>
        </b-form-group>
      </div>
      <div class="col-md-2">
        <b-form-group label="Đến ngày">
          <b-form-input type="date" v-model="filter.to"/>
        </b-form-group>
      </div>
      <div class="col-md-3 d-flex align-items-end">
        <b-button variant="primary" @click="load" class="mb-3 mr-2">
          <font-awesome-icon icon="search"/> Lọc
        </b-button>
        <b-button variant="success" @click="exportCSV" class="mb-3" :disabled="exporting">
          <font-awesome-icon icon="download"/> CSV
        </b-button>
      </div>
    </div>

    <!-- Table -->
    <b-table :items="items" :fields="fields" striped hover responsive small show-empty empty-text="Không có dữ liệu">
      <template #cell(createTime)="data">
        {{ data.value | moment('DD/MM HH:mm') }}
      </template>
      <template #cell(client)="data">
        <span v-if="data.value">{{ data.value.name }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(bot)="data">
        <span v-if="data.value">{{ data.value.name }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(platform)="data">
        <b-badge :variant="platformVariant(data.item)">{{ platformLabel(data.item) }}</b-badge>
      </template>
      <template #cell(customer)="data">
        <span v-if="data.value">{{ data.value.name || data.value.phone || '—' }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(question)="data">
        <span :title="data.item.message ? data.item.message.content : ''">{{ truncate(data.item.message ? data.item.message.content : '', 60) }}</span>
      </template>
      <template #cell(content)="data">
        <span :title="data.value">{{ truncate(data.value, 60) }}</span>
      </template>
      <template #cell(status)="data">
        <b-badge v-if="data.item.error" variant="danger">Lỗi</b-badge>
        <b-badge v-else-if="data.item.sendTime > 0" variant="success">Đã gửi</b-badge>
        <b-badge v-else variant="warning">Chờ</b-badge>
      </template>
      <template #cell(timing)="data">
        {{ data.item.timing && data.item.timing.total ? (data.item.timing.total / 1000).toFixed(1) + 's' : '—' }}
      </template>
      <template #cell(tokens)="data">
        {{ data.item.tokenUsage ? data.item.tokenUsage.totalTokens : '—' }}
      </template>
      <template #cell(actions)="data">
        <b-button size="sm" variant="outline-primary" @click="showDetail(data.item)">
          <font-awesome-icon icon="eye"/>
        </b-button>
      </template>
    </b-table>

    <!-- Pagination -->
    <div class="d-flex justify-content-between align-items-center" v-if="totalDocs > 0">
      <small class="text-muted">{{ totalDocs }} kết quả</small>
      <b-pagination v-model="page" :total-rows="totalDocs" :per-page="pageSize" @change="onPageChange"/>
    </div>

    <!-- Detail modal -->
    <b-modal v-model="showModal" title="Chi tiết hội thoại" size="lg" ok-only ok-title="Đóng">
      <div v-if="detail">
        <div class="row mb-3">
          <div class="col-md-6">
            <strong>Khách hàng:</strong> {{ detail.client ? detail.client.name : '—' }}
            <small class="text-muted" v-if="detail.client">({{ detail.client.domain }})</small>
          </div>
          <div class="col-md-6">
            <strong>Bot:</strong> {{ detail.bot ? detail.bot.name : '—' }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-md-6">
            <strong>Khách:</strong> {{ detail.customer ? detail.customer.name : '—' }}
            <small class="text-muted" v-if="detail.customer && detail.customer.phone">({{ detail.customer.phone }})</small>
          </div>
          <div class="col-md-6">
            <strong>Nền tảng:</strong>
            <b-badge :variant="platformVariant(detail)">{{ platformLabel(detail) }}</b-badge>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-md-4">
            <strong>Thời gian:</strong> {{ detail.createTime | moment('DD/MM/YYYY HH:mm:ss') }}
          </div>
          <div class="col-md-4">
            <strong>Xử lý:</strong> {{ detail.timing && detail.timing.total ? (detail.timing.total / 1000).toFixed(1) + 's' : '—' }}
            <small class="text-muted" v-if="detail.timing && detail.timing.setup">(setup: {{ detail.timing.setup }}ms)</small>
          </div>
          <div class="col-md-4">
            <strong>Trạng thái:</strong>
            <b-badge v-if="detail.error" variant="danger">Lỗi</b-badge>
            <b-badge v-else-if="detail.sendTime > 0" variant="success">Đã gửi</b-badge>
            <b-badge v-else variant="warning">Chờ</b-badge>
          </div>
        </div>
        <div class="mb-3" v-if="detail.tokenUsage">
          <strong>Token:</strong> prompt={{ detail.tokenUsage.promptTokens }}, completion={{ detail.tokenUsage.completionTokens }}, total={{ detail.tokenUsage.totalTokens }}
        </div>

        <hr/>

        <div class="mb-3">
          <strong>Khách hỏi:</strong>
          <div class="bg-light p-2 rounded mt-1" style="white-space: pre-wrap;">{{ detail.message ? detail.message.content : '—' }}
            <b-badge v-if="detail.message && detail.message.dataType !== 'text'" variant="info" class="ml-1">{{ detail.message.dataType }}</b-badge>
          </div>
        </div>
        <div class="mb-3">
          <strong>Bot trả lời:</strong>
          <div class="bg-light p-2 rounded mt-1" style="white-space: pre-wrap;">{{ detail.content || '—' }}</div>
        </div>
        <div class="mb-3" v-if="detail.errorMsg">
          <strong class="text-danger">Lỗi:</strong>
          <div class="bg-danger text-white p-2 rounded mt-1">{{ detail.errorMsg }}</div>
        </div>

        <!-- Steps -->
        <div class="mb-3" v-if="detail.steps && detail.steps.length">
          <strong>Steps ({{ detail.steps.length }}):</strong>
          <div class="mt-1" style="max-height: 300px; overflow-y: auto;">
            <div v-for="(step, i) in detail.steps" :key="i" class="border-bottom py-1">
              <small>
                <b-badge :variant="stepVariant(step.type)" class="mr-1">{{ step.type }}</b-badge>
                <span v-if="step.tool_calls && step.tool_calls.length">
                  <b-badge v-for="tc in step.tool_calls" :key="tc.name" variant="info" class="mr-1">{{ tc.name }}</b-badge>
                </span>
                <span v-if="step.content" class="text-muted">{{ truncate(typeof step.content === 'string' ? step.content : JSON.stringify(step.content), 120) }}</span>
              </small>
            </div>
          </div>
        </div>

        <!-- Tool errors -->
        <div class="mb-3" v-if="detail.errors && detail.errors.length">
          <strong class="text-warning">Tool errors:</strong>
          <ul class="mb-0 pl-3">
            <li v-for="(err, i) in detail.errors" :key="i">
              <small><b-badge variant="warning">{{ err.tool }}</b-badge> {{ err.message }}</small>
            </li>
          </ul>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
export default {
  data() {
    var now = new Date();
    var sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return {
      items: [],
      page: 1,
      pageSize: 20,
      totalDocs: 0,
      filter: {
        hasError: '',
        keyword: '',
        from: sevenDaysAgo.toISOString().split('T')[0],
        to: now.toISOString().split('T')[0],
      },
      errorOptions: [
        { value: '', text: 'Tất cả' },
        { value: 'true', text: 'Có lỗi' },
        { value: 'false', text: 'Không lỗi' },
      ],
      fields: [
        { key: 'createTime', label: 'Thời gian', class: 'text-nowrap' },
        { key: 'client', label: 'KH' },
        { key: 'bot', label: 'Bot' },
        { key: 'platform', label: 'Nền tảng', class: 'text-center' },
        { key: 'customer', label: 'Khách' },
        { key: 'question', label: 'Câu hỏi' },
        { key: 'content', label: 'Trả lời' },
        { key: 'status', label: 'TT', class: 'text-center' },
        { key: 'timing', label: 'TG', class: 'text-right' },
        { key: 'tokens', label: 'Token', class: 'text-right' },
        { key: 'actions', label: '', class: 'text-center' },
      ],
      showModal: false,
      detail: null,
      exporting: false,
    };
  },
  methods: {
    load() {
      var params = {
        page: this.page,
        pageSize: this.pageSize,
      };
      if (this.filter.hasError) params.hasError = this.filter.hasError;
      if (this.filter.keyword) params.keyword = this.filter.keyword;
      if (this.filter.from) params.from = new Date(this.filter.from).getTime();
      if (this.filter.to) params.to = new Date(this.filter.to).getTime() + 24 * 60 * 60 * 1000 - 1;

      this.$service.get('/ai-agent/logs', { params: params })
        .then(res => {
          this.items = res.data.docs;
          this.totalDocs = res.data.totalDocs;
        });
    },
    onPageChange(page) {
      this.page = page;
      this.load();
    },
    showDetail(item) {
      this.$service.get('/ai-agent/logs/' + item._id)
        .then(res => {
          this.detail = res.data;
          this.showModal = true;
        });
    },
    exportCSV() {
      this.exporting = true;
      var params = {};
      if (this.filter.hasError) params.hasError = this.filter.hasError;
      if (this.filter.keyword) params.keyword = this.filter.keyword;
      if (this.filter.from) params.from = new Date(this.filter.from).getTime();
      if (this.filter.to) params.to = new Date(this.filter.to).getTime() + 24 * 60 * 60 * 1000 - 1;

      this.$service.get('/ai-agent/export', { params: params, responseType: 'blob' })
        .then(function (res) {
          var url = window.URL.createObjectURL(new Blob([res.data]));
          var a = document.createElement('a');
          a.href = url;
          a.download = 'ai-agent-logs.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
        .finally(function () {
          this.exporting = false;
        }.bind(this));
    },
    truncate(str, len) {
      if (!str) return '—';
      return str.length > len ? str.substring(0, len) + '...' : str;
    },
    platformLabel(item) {
      if (item.account && item.account.type) {
        var labels = { facebook: 'Facebook', instagram: 'Instagram', zalo: 'Zalo', telegram: 'Telegram', whatsapp: 'WhatsApp' };
        return labels[item.account.type] || item.account.type;
      }
      if (item.livechat) return 'LiveChat';
      return '—';
    },
    platformVariant(item) {
      if (!item.account && item.livechat) return 'info';
      if (!item.account) return 'secondary';
      var variants = { facebook: 'primary', instagram: 'danger', zalo: 'info', telegram: 'info', whatsapp: 'success' };
      return variants[item.account.type] || 'secondary';
    },
    stepVariant(type) {
      if (type === 'HumanMessage') return 'light';
      if (type === 'AIMessage') return 'success';
      if (type === 'ToolMessage') return 'info';
      return 'secondary';
    },
  },
  mounted() {
    this.load();
  },
};
</script>
